import { useEffect, useState } from 'react';
import { api, type LoginMode } from '../lib/api';
import { useTerm } from '../lib/wording';

interface Props {
  /** Called after a successful sign-in so the caller can refetch. */
  onSignedIn: () => void;
}

interface CsrfResponse {
  csrfToken: string;
}

interface CallbackResponse {
  url?: string | null;
  error?: string | null;
}

/**
 * Inline sign-in for the referee PWA. Two render modes, picked by
 * `auth.loginMode`:
 *
 *  - off trusted networks: classic email + password + optional TOTP, posted
 *    to next-auth's `credentials` provider.
 *  - on a trusted network: dropdown of users + optional 4-digit PIN, posted
 *    to the `trusted-pin` provider, which validates the source IP again
 *    server-side before accepting.
 *
 * The sign-in HTML page lives only on the web app (count.flm9.club), but the
 * referee PWA is served from referee.flm9.club. Sending the user across hosts
 * would set the next-auth session cookie on the wrong domain, so we render a
 * local form here and POST directly to next-auth's CSRF + provider endpoints,
 * both of which are already proxied via the /api/ location.
 */
export function SignInScreen({ onSignedIn }: Props) {
  const [mode, setMode] = useState<LoginMode | null>(null);
  const [forcePassword, setForcePassword] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .loginMode()
      .then((m) => {
        if (!cancelled) setMode(m);
      })
      .catch(() => {
        // Network or backend issue — fall through to classic form so the
        // user is never locked out by a flaky tRPC call.
        if (!cancelled) setMode({ trusted: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const showDropdown = !forcePassword && mode?.trusted === true && (mode.users?.length ?? 0) > 0;

  return (
    <section className="space-y-3 rounded border border-amber-700 bg-amber-900/20 p-4">
      <h2 className="text-xl font-bold">Anmeldung erforderlich</h2>
      {showDropdown ? (
        <DropdownForm
          users={mode!.users!}
          onSignedIn={onSignedIn}
          onFallback={() => setForcePassword(true)}
        />
      ) : (
        <PasswordForm
          onSignedIn={onSignedIn}
          showSwitchBack={mode?.trusted === true}
          onSwitchBack={() => setForcePassword(false)}
        />
      )}
    </section>
  );
}

async function postNextAuth(
  endpoint: 'credentials' | 'trusted-pin',
  fields: Record<string, string>,
): Promise<{ ok: boolean }> {
  const csrfRes = await fetch('/api/auth/csrf', { credentials: 'include' });
  if (!csrfRes.ok) throw new Error('csrf_fetch_failed');
  const { csrfToken } = (await csrfRes.json()) as CsrfResponse;

  const body = new URLSearchParams({
    csrfToken,
    ...fields,
    callbackUrl: window.location.href,
    json: 'true',
    redirect: 'false',
  });
  const res = await fetch(`/api/auth/callback/${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
    // 302 → SPA navigation would discard our state; we already get the
    // session cookie from the response headers.
    redirect: 'manual',
  });

  // `redirect: 'manual'` makes a 3xx response surface as type === 'opaqueredirect'
  // with status 0. NextAuth treats credential failures as 302 → /sign-in?error=...,
  // and successes as 302 → callbackUrl. The session cookie is set either way,
  // so probe an authed endpoint to tell them apart.
  if (res.type === 'opaqueredirect' || res.status === 0) {
    const probe = await fetch('/api/auth/session', { credentials: 'include' });
    const session = (await probe.json().catch(() => null)) as { user?: unknown } | null;
    return { ok: !!(session && session.user) };
  }

  if (res.ok) {
    const data = (await res.json().catch(() => ({}))) as CallbackResponse;
    if (data.url) {
      const urlError = new URL(data.url).searchParams.get('error');
      if (urlError) return { ok: false };
    }
    return { ok: true };
  }
  return { ok: false };
}

interface PasswordFormProps {
  onSignedIn: () => void;
  showSwitchBack: boolean;
  onSwitchBack: () => void;
}

function PasswordForm({ onSignedIn, showSwitchBack, onSwitchBack }: PasswordFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [showTotp, setShowTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { ok } = await postNextAuth('credentials', {
        email,
        password,
        // Empty TOTP would trip authorize()'s zod regex, so only send when set.
        ...(totpCode ? { totpCode } : {}),
      });
      if (ok) {
        onSignedIn();
        return;
      }
      setError(
        showTotp
          ? 'Anmeldung fehlgeschlagen — Authenticator-Code prüfen.'
          : 'Anmeldung fehlgeschlagen — E-Mail / Passwort prüfen. Wenn 2FA aktiv ist, Code eintippen.',
      );
    } catch (err) {
      setError(`Anmeldung fehlgeschlagen — ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <p className="text-sm text-zinc-300">
        Mit deinem Standführer-Konto anmelden, um Trainings zu sehen.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="text-sm text-zinc-300">E-Mail</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-zinc-100"
            autoComplete="email"
            required
          />
        </label>
        <label className="block">
          <span className="text-sm text-zinc-300">Passwort</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-zinc-100"
            autoComplete="current-password"
            required
            minLength={8}
          />
        </label>
        {showTotp ? (
          <label className="block">
            <span className="text-sm text-zinc-300">Authenticator-Code</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 font-mono text-zinc-100"
              autoComplete="one-time-code"
              required
            />
          </label>
        ) : (
          <button
            type="button"
            onClick={() => setShowTotp(true)}
            className="block text-xs text-zinc-400 underline hover:text-emerald-400"
          >
            Authenticator-Code eingeben (nur falls 2FA aktiv ist)
          </button>
        )}
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
        >
          {loading ? 'Prüfe…' : 'Anmelden'}
        </button>
        {showSwitchBack && (
          <button
            type="button"
            onClick={onSwitchBack}
            className="block w-full text-xs text-zinc-400 underline hover:text-emerald-400"
          >
            Zurück zur Schnellanmeldung
          </button>
        )}
      </form>
    </>
  );
}

interface DropdownFormProps {
  users: { id: string; displayName: string; hasPin: boolean }[];
  onSignedIn: () => void;
  onFallback: () => void;
}

function DropdownForm({ users, onSignedIn, onFallback }: DropdownFormProps) {
  const t = useTerm();
  const [userId, setUserId] = useState(users[0]?.id ?? '');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  const selected = users.find((u) => u.id === userId);
  const needsPin = selected?.hasPin ?? false;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!userId) return;
    if (needsPin && !/^\d{4}$/.test(pin)) {
      setError('PIN besteht aus 4 Ziffern.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { ok } = await postNextAuth('trusted-pin', {
        userId,
        ...(needsPin ? { pin } : {}),
      });
      if (ok) {
        onSignedIn();
        return;
      }
      setError(needsPin ? 'PIN falsch oder zu viele Versuche.' : 'Anmeldung fehlgeschlagen.');
      setPin('');
    } catch (err) {
      setError(`Anmeldung fehlgeschlagen — ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  if (resetting && userId) {
    return (
      <PinResetFlow
        identifier={userId}
        userLabel={selected?.displayName ?? ''}
        onCancel={() => setResetting(false)}
        onDone={() => {
          setResetting(false);
          setPin('');
        }}
      />
    );
  }

  return (
    <>
      <p className="text-sm text-zinc-300">
        Vereinsnetzwerk erkannt — Schnellanmeldung möglich.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="text-sm text-zinc-300">{t('user')}</span>
          <select
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value);
              setPin('');
              setError(null);
            }}
            className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-zinc-100"
            required
          >
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
                {u.hasPin ? ' ⚿' : ''}
              </option>
            ))}
          </select>
        </label>
        {needsPin && (
          <label className="block">
            <span className="text-sm text-zinc-300">PIN</span>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]{4}"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 font-mono tracking-widest text-zinc-100"
              autoComplete="off"
              autoFocus
              required
            />
          </label>
        )}
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button
          type="submit"
          disabled={loading || !userId}
          className="w-full rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
        >
          {loading ? 'Prüfe…' : 'Anmelden'}
        </button>
        {needsPin && (
          <button
            type="button"
            onClick={() => setResetting(true)}
            className="block w-full text-xs text-zinc-400 underline hover:text-emerald-400"
          >
            PIN vergessen? Per SMS zurücksetzen
          </button>
        )}
        <button
          type="button"
          onClick={onFallback}
          className="block w-full text-xs text-zinc-400 underline hover:text-emerald-400"
        >
          Mit E-Mail und Passwort anmelden
        </button>
      </form>
    </>
  );
}

interface PinResetFlowProps {
  identifier: string;
  userLabel: string;
  onCancel: () => void;
  onDone: () => void;
}

/**
 * Two-step "I forgot my PIN" flow rendered inside the trusted-network
 * dropdown form. Mirrors the admin web app's component but uses the
 * PWA's bare-fetch `api` client rather than a tRPC react hook.
 */
function PinResetFlow({ identifier, userLabel, onCancel, onDone }: PinResetFlowProps) {
  const [step, setStep] = useState<'pin' | 'code' | 'done'>('pin');
  const [newPin, setNewPin] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitPin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\d{4}$/.test(newPin)) {
      setError('PIN besteht aus 4 Ziffern.');
      return;
    }
    setBusy(true);
    try {
      await api.requestPinReset(identifier, newPin);
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anfrage fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Bestätigungscode hat 6 Ziffern.');
      return;
    }
    setBusy(true);
    try {
      await api.confirmPinReset(identifier, code);
      setStep('done');
      setTimeout(onDone, 1500);
    } catch {
      setError('Code ungültig oder abgelaufen.');
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  if (step === 'done') {
    return (
      <div className="rounded border border-emerald-700 bg-emerald-900/40 p-3 text-sm text-emerald-100">
        PIN gesetzt. Du kannst dich jetzt mit dem neuen PIN anmelden.
      </div>
    );
  }

  if (step === 'code') {
    return (
      <form onSubmit={submitCode} className="space-y-3">
        <p className="text-xs text-zinc-300">
          {userLabel
            ? `Bestätigungscode an die Telefonnummer von „${userLabel}" gesendet (sofern vorhanden).`
            : 'Bestätigungscode an die hinterlegte Telefonnummer gesendet (sofern vorhanden).'}
        </p>
        <label className="block">
          <span className="text-sm text-zinc-300">Bestätigungscode (6 Ziffern)</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 font-mono tracking-widest text-zinc-100"
            autoComplete="one-time-code"
            autoFocus
            required
          />
        </label>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
        >
          {busy ? 'Prüfe…' : 'PIN setzen'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="block w-full text-xs text-zinc-400 underline hover:text-emerald-400"
        >
          Abbrechen
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submitPin} className="space-y-3">
      <p className="text-xs text-zinc-300">
        Wunsch-PIN eingeben — wir schicken einen 6-stelligen Bestätigungscode per SMS an die am
        Mitgliedsprofil hinterlegte Telefonnummer.
      </p>
      <label className="block">
        <span className="text-sm text-zinc-300">Neuer PIN (4 Ziffern)</span>
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]{4}"
          maxLength={4}
          value={newPin}
          onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
          className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 font-mono tracking-widest text-zinc-100"
          autoComplete="off"
          autoFocus
          required
        />
      </label>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <button
        type="submit"
        disabled={busy}
        className="w-full rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
      >
        {busy ? 'Sende SMS…' : 'Bestätigungscode anfordern'}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="block w-full text-xs text-zinc-400 underline hover:text-emerald-400"
      >
        Abbrechen
      </button>
    </form>
  );
}
